import { ReactElement, useContext, useRef, useState, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useForm } from 'react-hook-form';

import {
  TE_PAGE_TOPIC_CHAR_LIMIT,
  LP_PAGE_SUBJECT_CHAR_LIMIT,
} from '@lessonPlanner/constants';

import { TextAreaField, TextField } from '../components/form-fields';
import { SelectField } from '../components/form-fields';
import GenerateButton from '../components/GenerateButton';
import GenericBanner from '../components/GenericBanner';
import MainContent from '../components/MainContent';
import GenerateLinksButton from '../components/molecules/GenerateLinksButton';
import { GradeLevelDropdown } from '../components/Select';
import { PAGE_BUTTON_TEXT } from '../constants/buttonConstants';
import { GENERIC_UNPACKING_ITEM_CHAR_LIMIT } from '../constants/genericUnpacking';
import { routes } from '../constants/routes';
import {
  TOPIC_EXPLORER_PAGE_DESCRIPTION,
  TOPIC_EXPLORER_PAGE_EXPLORE,
  TOPIC_EXPLORER_TYPES,
  TOPIC_EXPLORER_DEFAULT_TYPE,
  TOPIC_EXPLORER_EXPLORER_DESCRIPTION,
  TOPIC_EXPLORER_BREAKDOWN_DESCRIPTION,
  TOPIC_EXPLORER_TITLE,
} from '../constants/topicExplorer';
import CreditContext from '../context/credits/context';
import { useGeneratePause } from '../context/generate/provider';
import { TopicExplorerProperties, PageKey } from '../enums/pageKey';
import { TopicExplorerType } from '../enums/topicExplorer.enum';
import useAutosizeTextArea from '../hooks/useAutoSizeTextArea';
import { useScrollToError } from '../hooks/useScrollToError';
import SidebarLayout from '../layouts/SidebarLayout';
import TopicExplorerSchema from '../schemas/TopicExplorerSchema';
import { topicExplorerProperties } from '../types/page';
import processInput from '../utils/formInput';
import { getLocalStorageItem } from '../utils/helper';
import { EVENTS } from '../utils/tracking';
import { trackEvent } from '../utils/tracking';

export default function TopicExplorer(): JSX.Element {
  const router = useRouter();

  const { status } = useSession();
  const isSignedIn = status === 'authenticated';
  const defaultValues = {
    topic: getLocalStorageItem(TopicExplorerProperties.topic),
    itemToUnpack: getLocalStorageItem(TopicExplorerProperties.itemToUnpack, ''),
    explorerType: getLocalStorageItem(
      TopicExplorerProperties.explorerType,
      TOPIC_EXPLORER_DEFAULT_TYPE,
    ),
    subject: getLocalStorageItem(TopicExplorerProperties.subject),
    gradeLevel: getLocalStorageItem(TopicExplorerProperties.gradeLevel),
  };

  const topicExplorerForm = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(TopicExplorerSchema),
  });

  const {
    setValue,
    watch,
    trigger,
    formState: { errors },
    clearErrors,
  } = topicExplorerForm;

  const defaultPremiumCreditCost = parseInt(
    process.env.NEXT_PUBLIC_GENERATE_PREMIUM_TOPIC_EXPLORER_COST,
  );

  const { context: creditContext } = useContext(CreditContext);
  const { userCredit } = creditContext;
  const canGeneratePremium = userCredit.paidCredits > 0;

  const topic = watch('topic');
  const itemToUnpack = watch('itemToUnpack');
  const explorerType = watch('explorerType');
  const subject = watch('subject');
  const gradeLevel = watch('gradeLevel');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const topicTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const itemToUnpackTextAreaRef = useRef<HTMLTextAreaElement>(null);

  useAutosizeTextArea(topicTextAreaRef.current, topic);
  useAutosizeTextArea(itemToUnpackTextAreaRef.current, itemToUnpack);

  const [explorerTypeSubLabel, setExplorerTypeSubLabel] = useState<string>('');

  useEffect(() => {
    if (explorerType === TopicExplorerType.EXPLORER) {
      setExplorerTypeSubLabel(TOPIC_EXPLORER_EXPLORER_DESCRIPTION);
    } else if (explorerType === TopicExplorerType.BREAKDOWN) {
      setExplorerTypeSubLabel(TOPIC_EXPLORER_BREAKDOWN_DESCRIPTION);
    } else {
      setExplorerTypeSubLabel('');
    }
  }, [explorerType]);

  const formContents = {
    topic: explorerType === TopicExplorerType.EXPLORER ? topic : '',
    itemToUnpack:
      explorerType === TopicExplorerType.BREAKDOWN ? itemToUnpack : '',
    explorerType,
    subject: explorerType === TopicExplorerType.BREAKDOWN ? subject : '',
    gradeLevel: explorerType === TopicExplorerType.BREAKDOWN ? gradeLevel : '',
  };

  function handleclickTopup() {
    trackEvent(EVENTS.CLICK_TOPUP_LINK, {
      from: 'Lesson Plan Page',
    });
    router.push(routes.PRICING);
  }

  async function triggerValidation(fieldName) {
    await trigger(fieldName);
  }

  async function updateFieldValue(fieldName, value) {
    setValue(fieldName, value);
    if (fieldName === 'explorerType') {
      if (value === TopicExplorerType.EXPLORER) {
        clearErrors(['subject', 'gradeLevel', 'itemToUnpack']);
      } else if (value === TopicExplorerType.BREAKDOWN) {
        clearErrors(['topic']);
      }
    }
  }

  async function customFormValidator() {
    await trigger();
  }

  const hasErrors = Object.entries(errors).length > 0;
  useScrollToError(errors);

  const { pausedButtonText, isGeneratePaused } = useGeneratePause();
  const buttonText = isGeneratePaused
    ? pausedButtonText
    : PAGE_BUTTON_TEXT[PageKey.topicExplorer];

  const isGenerateButtonDisabled = isLoading || hasErrors || isGeneratePaused;

  const banner = (
    <GenericBanner
      showTutorial
      content={TOPIC_EXPLORER_PAGE_DESCRIPTION}
      isPremium={canGeneratePremium}
      title={TOPIC_EXPLORER_TITLE}
      variation={explorerType?.toLowerCase()}
    />
  );

  return (
    <MainContent
      banner={banner}
      creditCost={defaultPremiumCreditCost}
      formContents={formContents as topicExplorerProperties}
      pageKey={PageKey.topicExplorer}
      setIsLoading={setIsLoading}
    >
      <>
        <div className="col-span-full">
          <SelectField
            className="col-span-full"
            error={!!errors.explorerType}
            id="explorerType"
            label="Explorer Type"
            name="explorerType"
            value={explorerType}
            errorMessage={
              !!errors.explorerType && 'Please select Explorer Type'
            }
            onBlur={() => triggerValidation('explorerType')}
            onChange={(value) => updateFieldValue('explorerType', value)}
          >
            {TOPIC_EXPLORER_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </SelectField>
          {explorerTypeSubLabel && (
            <div className="mt-1 text-sm text-gray-500">
              {explorerTypeSubLabel}
            </div>
          )}
        </div>

        {explorerType === TopicExplorerType.EXPLORER && (
          <TextAreaField
            showSubLabel
            className="col-span-full"
            error={!!errors.topic}
            errorMessage={!!errors.topic && 'Please enter the Topic'}
            id="topic"
            isMaxLengthReached={topic?.length >= TE_PAGE_TOPIC_CHAR_LIMIT}
            label="Topic"
            maxLength={TE_PAGE_TOPIC_CHAR_LIMIT}
            name="topic"
            placeholder="Input the topic to explore"
            rows={2}
            subLabel={TOPIC_EXPLORER_PAGE_EXPLORE}
            textAreaRef={topicTextAreaRef}
            type="topic"
            value={topic}
            onBlur={() => {
              processInput(
                updateFieldValue,
                triggerValidation,
                'topic',
                topic?.trim() || '',
              );
            }}
            onChange={(e) =>
              processInput(
                updateFieldValue,
                triggerValidation,
                'topic',
                e.target.value,
              )
            }
          />
        )}

        {explorerType === TopicExplorerType.BREAKDOWN && (
          <>
            <TextField
              className="col-span-full"
              errorMessage={!!errors.subject && 'Please enter the Subject'}
              id="subject"
              isMaxLengthReached={subject?.length >= LP_PAGE_SUBJECT_CHAR_LIMIT}
              label="Subject"
              maxLength={LP_PAGE_SUBJECT_CHAR_LIMIT}
              name="subject"
              placeholder="Input the subject"
              type="subject"
              value={subject}
              onBlur={() => {
                processInput(
                  updateFieldValue,
                  triggerValidation,
                  'subject',
                  subject?.trim() || '',
                );
              }}
              onChange={(e) =>
                processInput(
                  updateFieldValue,
                  triggerValidation,
                  'subject',
                  e.target.value,
                )
              }
            />

            <GradeLevelDropdown
              hasCollege
              error={!!errors.gradeLevel}
              label="Grade Level"
              value={gradeLevel}
              errorMessage={
                !!errors.gradeLevel && 'Please select the Grade Level'
              }
              onChange={(value) => updateFieldValue('gradeLevel', value)}
              onBlur={() => {
                triggerValidation('gradeLevel');
              }}
            />

            <TextAreaField
              showSubLabel
              className="col-span-full"
              error={!!errors.itemToUnpack}
              id="itemToUnpack"
              label="Topic to Break Down"
              maxLength={GENERIC_UNPACKING_ITEM_CHAR_LIMIT}
              name="itemToUnpack"
              placeholder="Input the topic to break down into 8 sub-topics"
              rows={2}
              subLabel="Add the main topic to be broken down into 8 sub-topics"
              textAreaRef={itemToUnpackTextAreaRef}
              type="itemToUnpack"
              value={itemToUnpack}
              errorMessage={
                !!errors.itemToUnpack && 'Please enter the Topic to break down'
              }
              isMaxLengthReached={
                itemToUnpack?.length >= GENERIC_UNPACKING_ITEM_CHAR_LIMIT
              }
              onBlur={() => {
                processInput(
                  updateFieldValue,
                  triggerValidation,
                  'itemToUnpack',
                  itemToUnpack?.trim() || '',
                );
              }}
              onChange={(e) =>
                processInput(
                  updateFieldValue,
                  triggerValidation,
                  'itemToUnpack',
                  e.target.value,
                )
              }
            />
          </>
        )}

        <div className="col-span-full">
          <div className="flex flex-col">
            <GenerateButton
              buttonText={buttonText}
              creditCost={defaultPremiumCreditCost}
              disabled={isGenerateButtonDisabled}
              isPremium={canGeneratePremium}
              onClick={customFormValidator}
            />

            {!canGeneratePremium && (
              <GenerateLinksButton
                handleclickTopup={handleclickTopup}
                isSignedIn={isSignedIn}
              />
            )}
          </div>
        </div>
      </>
    </MainContent>
  );
}

TopicExplorer.getLayout = function getLayout(page: ReactElement) {
  return <SidebarLayout>{page}</SidebarLayout>;
};
